import { lazy } from 'react';

/**
 * Wraps React.lazy() to handle stale chunk errors after deployments.
 *
 * When a browser tab is idle for a long time and a new version of the app
 * is deployed, Vite's content-hashed chunk files (e.g. About-Bxy3f.js) are
 * replaced with new names. If the user returns to the old tab, dynamic
 * import() calls will 404, bypassing the React ErrorBoundary and producing
 * a blank page.
 *
 * This wrapper catches that failure and forces a one-time hard reload to
 * pick up the new chunks. A sessionStorage flag prevents infinite reload
 * loops if the error persists after the refresh.
 */
export function lazyWithRetry(importFn) {
  return lazy(async () => {
    const alreadyRefreshed = sessionStorage.getItem('chunkLoadRefreshed') === 'true';
    try {
      const component = await importFn();
      sessionStorage.removeItem('chunkLoadRefreshed');
      return component;
    } catch (error) {
      if (!alreadyRefreshed) {
        sessionStorage.setItem('chunkLoadRefreshed', 'true');
        window.location.reload();
        // Return a no-op component while the reload happens
        return { default: () => null };
      }
      // Already tried reloading — let the ErrorBoundary handle it
      throw error;
    }
  });
}
